import React, { createContext, useContext, useState } from 'react';
import { ok } from 'node:assert/strict';
import type { FC, ReactNode } from 'react';
import type { Env, WixAstroConfig } from '@wix/cli-astro-definitions';
import type { DefinedWixConfig, PluginsInterface } from '@wix/cli-plugin-core';
import { readEnv } from './files/env.js';

export interface ProjectModel {
  config: WixAstroConfig;
  env: Env;
  projectFolder: string;
  releaseAppId: string;
  pluginsInterface?: PluginsInterface;
}

export async function loadProjectModel(
  projectFolder: string,
  definedWixConfig: DefinedWixConfig
): Promise<ProjectModel> {
  const env = await readEnv(projectFolder);
  const { config, pluginsInterface } = definedWixConfig;

  return {
    projectFolder,
    config,
    env,
    releaseAppId:
      config.projectType === 'App' && config.projectId
        ? config.projectId
        : config.appId,
    pluginsInterface,
  };
}

interface ProjectModelState {
  model: ProjectModel;
}

const ProjectModelContext = createContext<ProjectModelState | null>(null);

interface Props {
  value: ProjectModel;
  children: ReactNode;
}

export const ProjectModelProvider: FC<Props> = (props) => {
  const [model] = useState(props.value);

  return (
    <ProjectModelContext.Provider value={{ model }}>
      {props.children}
    </ProjectModelContext.Provider>
  );
};

export function useProjectModel(): ProjectModelState {
  const context = useContext(ProjectModelContext);
  if (context == null) {
    throw new Error(
      'useProjectModel() cannot be used without a <ProjectModelProvider>'
    );
  }
  return context;
}

export function useSiteConfig() {
  const {
    model: { config },
  } = useProjectModel();

  ok(config.projectType === 'Site', 'Project is not a Site');

  return config;
}
